import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  structuredData?: Record<string, unknown>;
}

const defaultTitle = "Kupoler för trädgård, glamping, växthus, uterum & boende";
const defaultDescription = "Diamond Domes är Sveriges ledande leverantör av kupoler för boende, glamping, växthus och uterum. Geodetiska kupoler — bygglovsfria, året runt.";
const defaultOgImage = "/lovable-uploads/40070712-4bc0-4817-a20b-7811cc3ea529.webp";
const BASE_URL = 'https://www.diamonddomes.se';

// Normalisera path med konsekvent slash-policy
// Root: trailing slash (/), undersidor: ingen trailing slash (/shop)
const normalizePath = (path: string): string => {
  if (path === '/' || path === '') {
    return '/';
  }
  return path.replace(/\/+$/, '');
};

// Bygg ren canonical/hreflang URL (endast ?lang= bevaras, allt annat tas bort)
const buildLanguageUrl = (basePath: string, lang: 'sv' | 'en' | 'de'): string => {
  const normalizedPath = normalizePath(basePath);
  if (lang === 'sv') {
    return `${BASE_URL}${normalizedPath}`;
  }
  return `${BASE_URL}${normalizedPath}?lang=${lang}`;
};

export const SEOHead = ({
  title = defaultTitle,
  description = defaultDescription,
  canonical,
  ogImage = defaultOgImage,
  noIndex = false,
  structuredData,
}: SEOHeadProps) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    // Update document title
    document.title = title;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // === SELF-REFERENCING CANONICAL ===
    // Rensar ALLA query params (utm, fbclid, gclid, etc.) och bevarar endast ?lang= om ej svenska
    const canonicalUrl = canonical || buildLanguageUrl(currentPath, language);
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Update Open Graph tags
    const ogTags = {
      'og:title': title,
      'og:description': description,
      'og:image': ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`,
      'og:type': 'website',
      'og:url': canonicalUrl,
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // Update Twitter card tags
    const twitterTags = {
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`,
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // Handle robots meta tag for noIndex pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      robotsMeta.remove();
    }

    // === HREFLANG-TAGGAR (ersätt befintliga, ta INTE bort vid unmount) ===
    const hreflangConfig = [
      { lang: 'sv', href: buildLanguageUrl(currentPath, 'sv') },
      { lang: 'en', href: buildLanguageUrl(currentPath, 'en') },
      { lang: 'de', href: buildLanguageUrl(currentPath, 'de') },
      { lang: 'x-default', href: buildLanguageUrl(currentPath, 'sv') }, // Default = svenska
    ];
    
    hreflangConfig.forEach(({ lang, href }) => {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    });

    // Handle JSON-LD structured data
    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (structuredData) {
      if (existingScript) {
        existingScript.textContent = JSON.stringify(structuredData);
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(structuredData);
        document.head.appendChild(script);
      }
    } else if (existingScript) {
      existingScript.remove();
    }

    // Cleanup: endast JSON-LD tas bort vid unmount (hreflang stannar kvar för stabilitet)
    return () => {
      const script = document.querySelector('script[data-seo-jsonld]');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, canonical, ogImage, noIndex, structuredData, language]);

  return null;
};

// Pre-defined SEO configurations for common pages
export const seoConfig = {
  home: {
    title: "Kupoler för trädgård, glamping, växthus, uterum & boende",
    description: "Diamond Domes är Sveriges ledande leverantör av kupoler för boende, glamping, växthus och uterum. Geodetiska kupoler — bygglovsfria, året runt.",
  },
  catalog: {
    title: "Katalog – vårt utbud av kupoler | Diamond Domes",
    description: "Utforska vårt utbud av geodetiska kupoler. Panoramakupoler, biokeramiska Geoship-kupoler, Nova Domes och handgjorda lampor.",
  },
  shop: {
    title: "Butik – köp kupoler & tillbehör | Diamond Domes",
    description: "Köp geodetiska kupoler och tillbehör. Panoramakupoler från 10m², biokeramiska kupoler och handgjorda dome-lampor.",
  },
  about: {
    title: "Om oss - Diamond Domes",
    description: "Diamond Domes skapar platser där människor kan känna sig närvarande och levande. Lär känna oss och vår vision för geodetiska kupoler.",
  },
  contact: {
    title: "Kontakt - Diamond Domes",
    description: "Kontakta Diamond Domes för frågor om geodetiska kupoler. Vi hjälper dig hitta rätt dome-lösning för dina behov.",
  },
  polycarbonate: {
    title: "Panoramakupoler — runda utrymmen som förhöjer livskvaliteten",
    description: "Svensktillverkade panoramakupoler 10–30 m². Geodetisk, ramlös konstruktion för växthus, uterum & glamping. Tål nordisk snölast. Året-runt-bruk.",
  },
  bioceramic: {
    title: "Biokeramiska kupoler - Geoship | Diamond Domes",
    description: "Geoship biokeramiska kupoler. Hållbara, miljövänliga geodetiska hem byggda med revolutionerande biokeramisk teknik.",
  },
  nova: {
    title: "Nova Domes - Astro Series | Diamond Domes",
    description: "Nova Domes Astro Series. Premiuminredda geodetiska kupoler för exklusiva boenden och glamping-upplevelser.",
  },
  lampor: {
    title: "Dome Lampor - Handgjorda lampor | Diamond Domes",
    description: "Handgjorda dome-lampor i trä och koppar. Unika belysningsdesigner inspirerade av geodetiska former.",
  },
  experiences: {
    title: "Dome-upplevelser - Besök, övernatta, bli värd | Diamond Domes",
    description: "Upplev en geodetisk kupol. Besök en dome, övernatta i en dome eller bli dome-värd och hyr ut din egen kupol.",
  },
  blog: {
    title: "Blogg - Nyheter & inspiration | Diamond Domes",
    description: "Läs om geodetiska kupoler, hållbart boende och inspiration för dome-liv. Nyheter och tips från Diamond Domes.",
  },
  // Legal pages
  angerratt: {
    title: "Ångerrätt - Returpolicy | Diamond Domes",
    description: "Information om din ångerrätt vid köp från Diamond Domes. 14 dagars returrätt enligt distansavtalslagen.",
  },
  cookiepolicy: {
    title: "Cookiepolicy | Diamond Domes",
    description: "Så använder Diamond Domes cookies på vår webbplats. Läs om vilka cookies vi använder och hur du hanterar dem.",
  },
  integritetspolicy: {
    title: "Integritetspolicy - GDPR | Diamond Domes",
    description: "Diamond Domes värnar om din integritet. Läs hur vi samlar in, använder och skyddar dina personuppgifter enligt GDPR.",
  },
  kopvillkor: {
    title: "Köpvillkor | Diamond Domes",
    description: "Köpvillkor för Diamond Domes webshop. Information om beställning, betalning, leverans och garantier.",
  },
  leveranspolicy: {
    title: "Leveranspolicy - Frakttider | Diamond Domes",
    description: "Information om leveranstider och frakt för Diamond Domes produkter. Alltid gratis frakt på domes inom Sverige.",
  },
  // Experience pages
  visitDome: {
    title: "Besök en Dome - Upplev en kupol | Diamond Domes",
    description: "Besök en geodetisk kupol nära dig. Upplev känslan av att vara i en dome innan du bestämmer dig.",
  },
  stayInDome: {
    title: "Övernatta i Dome - Glamping | Diamond Domes",
    description: "Bo i en geodetisk kupol. Unika glampingupplevelser i natursköna miljöer. Boka din dome-övernattning.",
  },
  becomeHost: {
    title: "Bli Dome-värd - Hyr ut din kupol | Diamond Domes",
    description: "Bli dome-värd och tjäna pengar på din geodetiska kupol. Registrera dig och nå gäster som söker unika boenden.",
  },
  // Shop flow pages (noIndex)
  cart: {
    title: "Kundvagn | Diamond Domes",
    description: "Granska produkterna i din kundvagn hos Diamond Domes och gå vidare till kassan för att slutföra ditt köp av kupoler eller tillbehör.",
    noIndex: true,
  },
  checkout: {
    title: "Kassa | Diamond Domes",
    description: "Slutför din beställning hos Diamond Domes – ange leverans- och betalningsuppgifter och bekräfta ditt köp av kupoler, lampor eller tillbehör.",
    noIndex: true,
  },
  orderConfirmation: {
    title: "Orderbekräftelse | Diamond Domes",
    description: "Tack för din beställning hos Diamond Domes. Här ser du en bekräftelse på ditt köp och nästa steg för leverans av dina produkter.",
    noIndex: true,
  },
  bookingConfirmation: {
    title: "Bokningsbekräftelse | Diamond Domes",
    description: "Tack för din bokning hos Diamond Domes. Här ser du en bekräftelse på din dome-vistelse och information om incheckning och värd.",
    noIndex: true,
  },
  becomePartner: {
    title: "Bli partner med oss | Diamond Domes Partnerprogram",
    description: "Ansök till Diamond Domes partnerprogram. Tjäna kommission genom att dela våra Polykarbonat Domes på sociala medier. För företag med F-skatt.",
  },
  // Use-case landningssidor
  useCaseHub: {
    title: "Användningsområden för geodetiska kupoler | Diamond Domes",
    description: "Upptäck hur en geodetisk kupol kan användas som uterum, växthus, glamping eller trädgårdsrum. Inspiration, modeller och specifikationer.",
  },
  useCaseUterum: {
    title: "Kupol som uterum — året runt i trädgården | Diamond Domes",
    description: "Bygg ett uterum i form av en geodetisk kupol. Snölast, isolering, ljus och 360°-utsikt. Se modeller, priser och kundberättelser.",
  },
  useCaseVaxthus: {
    title: "Kupol som växthus & orangeri — odla året runt | Diamond Domes",
    description: "Geodetisk kupol som växthus eller orangeri. Maximal ljusinsläpp, snöstark, premium-design. Se modeller från 35 000 kr inkl. moms.",
  },
  useCaseGlamping: {
    title: "Glamping i kupol — bygg en unik upplevelse | Diamond Domes",
    description: "Skapa en glamping-upplevelse med en geodetisk kupol. För privat uthyrning, hotell och campingar. ROI-kalkyl, modeller och bygg-guide.",
  },
  useCaseTradgardsrum: {
    title: "Trädgårdskupol — sunroom, uterum & extra rum | Diamond Domes",
    description: "Trädgårdskupol som sunroom, uterum och flexibelt extra rum. Geodetiska kupoler från 10 m² — bygglovsfria, året runt, 360° utsikt.",
  },
};
